import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Star, Edit, Trash2, Eye } from 'lucide-react';
import {
  DataTable,
  type TableColumn,
  type TableAction,
  type PaginationInfo,
} from '@/components/shared/data-table';
import { useDeletePropertyMutation } from '@/hooks/queries/useProperties';
import { usePropertyTypesDropdownQuery } from '@/hooks/queries/useDropdowns';
import { useCanManageProperties } from '@/hooks/usePermissions';
import type { ApiProperty } from '@/types/api';
import { useTranslation } from 'react-i18next';
import { BACKEND_BASE } from '@/config/constants';

// ============================================================================
// Helper Components
// ============================================================================

const StarRating = ({ count }: { count: number }) => (
  <div className="flex items-center gap-1">
    <span className="text-sm font-medium">{count}</span>
    <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-yellow-400" />
  </div>
);

const PropertyInfo = ({ property }: { property: ApiProperty }) => {
  // Prepend BACKEND_BASE to relative logo_url path
  const imageUrl = property.logo_url
    ? `${BACKEND_BASE}${property.logo_url}`
    : `https://placehold.co/40x40/e2e8f0/64748b?text=${property.name.charAt(0).toUpperCase()}`;

  return (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      {property.logo_url ? (
        <img
          src={imageUrl}
          width={40}
          height={40}
          alt={`${property.name} logo`}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-md object-cover shrink-0"
          onError={e => {
            const target = e.target as HTMLImageElement;
            target.src = `https://placehold.co/40x40/e2e8f0/64748b?text=${property.name.charAt(0).toUpperCase()}`;
          }}
        />
      ) : (
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground shrink-0">
          {property.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm sm:text-base truncate">
          {property.name}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ isActive }: { isActive: boolean }) => {
  const { t } = useTranslation();

  return (
    <Badge
      variant={isActive ? 'secondary' : 'destructive'}
      className="text-xs px-2 py-1"
    >
      {isActive
        ? t('common.active', 'Active')
        : t('common.inactive', 'Inactive')}
    </Badge>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface PropertyListProps {
  properties: ApiProperty[];
  isLoading?: boolean;
  error?: Error | null;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
}

export default function PropertyList({
  properties,
  isLoading = false,
  error = null,
  pagination,
  onPageChange,
}: PropertyListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Permission check
  const canManage = useCanManageProperties();

  // Use property types dropdown for display
  const { data: propertyTypes = [] } = usePropertyTypesDropdownQuery();

  // API mutations
  const deletePropertyMutation = useDeletePropertyMutation({
    onSuccessCallback: () => {
      setDeleteId(null);
    },
  });

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleView = (property: ApiProperty) => {
    navigate(`/properties/${property.id}`);
  };

  const handleEdit = (property: ApiProperty) => {
    navigate(`/properties/edit/${property.id}`);
  };

  const handleDelete = (property: ApiProperty) => {
    setDeleteId(property.id);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deletePropertyMutation.mutate(deleteId);
    }
  };

  // ============================================================================
  // Table Configuration
  // ============================================================================

  const getPropertyTypeName = (property_type_id: number) => {
    const propertyType = propertyTypes.find(pt => pt.id === property_type_id);
    return propertyType?.name || 'N/A';
  };

  const columns: TableColumn<ApiProperty>[] = [
    {
      key: 'name',
      label: t('properties.management.columns.property', 'Property'),
      className: 'min-w-[180px]',
      render: property => <PropertyInfo property={property} />,
    },
    {
      key: 'property_type_id',
      label: t('properties.management.columns.type', 'Type'),
      className: 'min-w-[120px]',
      render: property => (
        <Badge variant="outline" className="capitalize text-xs sm:text-sm">
          {property.property_type?.name ||
            getPropertyTypeName(property.property_type_id)}
        </Badge>
      ),
    },
    {
      key: 'stars',
      label: t('properties.management.columns.rating', 'Rating'),
      className: 'min-w-[80px]',
      render: property =>
        property.stars ? (
          <StarRating count={property.stars} />
        ) : (
          <span className="text-xs sm:text-sm text-muted-foreground">N/A</span>
        ),
    },
    {
      key: 'location',
      label: t('properties.management.columns.location', 'Location'),
      className: 'min-w-[150px]',
      render: property => (
        <div
          className="text-sm max-w-[150px] truncate"
          title={property.location || ''}
        >
          {property.location || 'N/A'}
        </div>
      ),
    },
    {
      key: 'contract_start',
      label: t('properties.management.columns.startDate', 'Start Date'),
      className: 'min-w-[130px]',
      render: property => (
        <div className="text-sm whitespace-nowrap">
          {property.contract_start ? (
            new Date(property.contract_start).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          ) : (
            <span className="text-xs text-muted-foreground">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'contract_end',
      label: t('properties.management.columns.endDate', 'End Date'),
      className: 'min-w-[130px]',
      render: property => (
        <div className="text-sm whitespace-nowrap">
          {property.contract_end ? (
            new Date(property.contract_end).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          ) : (
            <span className="text-xs text-muted-foreground">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'is_active',
      label: t('properties.management.columns.status', 'Status'),
      className: 'text-center min-w-[100px]',
      render: property => <StatusBadge isActive={property.is_active} />,
    },
  ];

  const actions: TableAction<ApiProperty>[] = [
    {
      label: t('common.view', 'View'),
      icon: Eye,
      onClick: handleView,
    },
    ...(canManage
      ? [
          {
            label: t('common.edit', 'Edit'),
            icon: Edit,
            onClick: handleEdit,
          },
          {
            label: t('common.delete', 'Delete'),
            icon: Trash2,
            onClick: handleDelete,
            variant: 'destructive' as const,
            separator: true,
          },
        ]
      : []),
  ];

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      <DataTable
        data={properties}
        columns={columns}
        actions={actions}
        isLoading={isLoading}
        error={error}
        pagination={pagination}
        onPageChange={onPageChange}
        loadingMessage={t(
          'properties.management.loading',
          'Loading properties...'
        )}
        errorMessage={t(
          'properties.management.error',
          'Failed to load properties'
        )}
        emptyMessage={t(
          'properties.management.noProperties',
          'No properties found'
        )}
        tableCaption={t(
          'properties.management.tableCaption',
          'Properties list'
        )}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">
              {t('properties.management.deleteDialog.title', 'Delete Property')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {t(
                'properties.management.deleteDialog.description',
                'This action cannot be undone. This will permanently delete the property and all associated data.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
